import { faWarning } from '@fortawesome/free-solid-svg-icons';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import OperationsLogsList from '@/components/OperationsLogsList';
import PageHeader from '@/components/PageHeader';
import Pagination from '@/components/Pagination';
import { RewardCardStatus } from '@/constants/rewardCard';
import usePagination from '@/hooks/usePagination';
import { useOperationsLogs, useRewardCardAccountsForPerson } from '@/hooks/useRewardCards';
import PersonContext from '@/pages/Person/PersonContext';
import NewRewardCardButton from '@/pages/RewardCardList/components/NewRewardCardButton';
import { Banner } from '@/ui';
import Container from '@/ui/components/Container';
import Heading from '@/ui/components/Heading/Heading';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';
import formatPersonName from '@/utils/formatPersonName';

import RewardCardItem from './components/RewardCardItem';

const RewardCardList = () => {
  const { person } = useContext(PersonContext);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [searchParams, setSearchParams] = useSearchParams({ page: '1' });

  const operationsLogsParams = useMemo(() => ({ is_completed: false }), []);
  const { data: operationsLogsResponse } = useOperationsLogs({
    personId: person.id,
    params: operationsLogsParams
  });

  const { currentPage, goToPage, nextPage, prevPage } = usePagination(
    +(searchParams.get('page') ?? '1'),
    totalPages
  );

  useEffect(() => {
    setSearchParams((currentParams) => {
      currentParams.set('page', currentPage.toString());
      return currentParams;
    });
  }, [currentPage, setSearchParams]);

  const {
    data: rewardCardAccountsResponse,
    isPending: isPendingRewardCardAccounts,
    isSuccess: isSuccessRewardCardAccounts
  } = useRewardCardAccountsForPerson(person.id, { page: currentPage.toString() });

  useEffect(() => {
    if (isSuccessRewardCardAccounts) {
      if (rewardCardAccountsResponse?.page) {
        setSearchParams((currentParams) => {
          currentParams.set('page', rewardCardAccountsResponse.page.toString());
          return currentParams;
        });
      }

      if (rewardCardAccountsResponse?.total_pages) {
        setTotalPages(rewardCardAccountsResponse.total_pages);
      }
    }
  }, [isSuccessRewardCardAccounts, rewardCardAccountsResponse, setSearchParams]);

  const currentActiveCard = useMemo(() => {
    return rewardCardAccountsResponse?.results.find(
      (account) => account.current_card?.card_status === RewardCardStatus.ACTIVE
    );
  }, [rewardCardAccountsResponse?.results]);

  const hasCurrentActiveCard = useMemo(() => {
    return !!currentActiveCard;
  }, [currentActiveCard]);

  return (
    <Container type="page">
      <PageHeader headingText="Reward Card Accounts" className="sr-only" />
      <NewRewardCardButton />

      {currentActiveCard?.will_terminate_at && (
        <Banner
          icon={faWarning}
          color="orange"
          label={`Reward card will be automatically closed on ${new Date(
            currentActiveCard.will_terminate_at
          ).toLocaleString('en-US', { dateStyle: 'medium' })}`}
        />
      )}

      <OperationsLogsList />

      <Pagination
        currentPage={currentPage}
        goToPage={goToPage}
        nextPage={nextPage}
        prevPage={prevPage}
        totalPages={totalPages}>
        {isPendingRewardCardAccounts ? (
          <LoadingSkeleton spinnerProps={{ label: 'Loading reward card accounts...' }} />
        ) : !rewardCardAccountsResponse?.results.length ? (
          <div className="text-center p-8">
            <Heading
              className="mp-sensitive"
              text={`No accounts found for ${formatPersonName({ person })}`}
              value={3}
            />
          </div>
        ) : (
          <ul className="mt-2" role="list">
            {rewardCardAccountsResponse?.results.map((account) => {
              return (
                <li key={account.account_id}>
                  <RewardCardItem
                    rewardCardAccount={account}
                    hasCurrentActiveCard={hasCurrentActiveCard}
                    operationsLogs={operationsLogsResponse?.results.filter(
                      (operationsLog) => operationsLog.pex_resource_id === account.pex_account_id
                    )}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </Pagination>
    </Container>
  );
};

export default RewardCardList;
